import axios from 'axios';

/**
 * General API service for handling non-authentication API calls.
 * 
 * This service manages API requests for routes, bookings, and other
 * application features that require backend communication.
 * 
 * @author BagABoard Team
 */

// Base API URL - will use proxy in development
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const apiService = {
  // Route-related API calls
  routes: {
    /**
     * Search for routes between two countries.
     * 
     * @param {string} originCountry - Origin country name
     * @param {string} destinationCountry - Destination country name
     * @returns {Promise<Object>} - Routes search response
     */
    async searchRoutes(originCountry, destinationCountry) {
      try {
        const response = await apiClient.get('/routes/search', {
          params: { originCountry, destinationCountry }
        });
        return response.data;
      } catch (error) {
        console.error('Route search error:', error);
        throw error;
      }
    },

    /**
     * Get route details by ID.
     * 
     * @param {number} routeId - Route ID
     * @returns {Promise<Object>} - Route details
     */
    async getRoute(routeId) {
      try {
        const response = await apiClient.get(`/routes/${routeId}`);
        return response.data;
      } catch (error) {
        console.error('Get route error:', error);
        throw error;
      }
    },

    /**
     * Calculate shipping cost for a route.
     * 
     * @param {number} routeId - Route ID
     * @param {number} totalWeight - Total weight of bags
     * @param {number} numberOfBags - Number of bags
     * @returns {Promise<Object>} - Cost calculation response
     */
    async calculateCost(routeId, totalWeight, numberOfBags) {
      try {
        const response = await apiClient.get(`/routes/${routeId}/calculate-cost`, {
          params: { totalWeight, numberOfBags }
        });
        return response.data;
      } catch (error) {
        console.error('Cost calculation error:', error);
        throw error;
      }
    },

    /**
     * Get all origin countries.
     * 
     * @returns {Promise<Object>} - Origin countries list
     */
    async getOriginCountries() {
      try {
        const response = await apiClient.get('/routes/countries/origins');
        return response.data;
      } catch (error) {
        console.error('Get origin countries error:', error);
        throw error;
      }
    },

    /**
     * Get all destination countries.
     * 
     * @returns {Promise<Object>} - Destination countries list
     */
    async getDestinationCountries() {
      try {
        const response = await apiClient.get('/routes/countries/destinations');
        return response.data;
      } catch (error) {
        console.error('Get destination countries error:', error);
        throw error;
      }
    },

    /**
     * Get origin cities for a country.
     * 
     * @param {string} country - Country name
     * @returns {Promise<Object>} - Origin cities list
     */
    async getOriginCities(country) {
      try {
        const response = await apiClient.get(`/routes/cities/origins/${country}`);
        return response.data;
      } catch (error) {
        console.error('Get origin cities error:', error);
        throw error;
      }
    },

    /**
     * Get destination cities for a country.
     * 
     * @param {string} country - Country name
     * @returns {Promise<Object>} - Destination cities list
     */
    async getDestinationCities(country) {
      try {
        const response = await apiClient.get(`/routes/cities/destinations/${country}`);
        return response.data;
      } catch (error) {
        console.error('Get destination cities error:', error);
        throw error;
      }
    }
  },

  // Booking-related API calls
  bookings: {
    /**
     * Create a new booking.
     * 
     * @param {Object} bookingData - Booking creation data
     * @returns {Promise<Object>} - Created booking
     */
    async createBooking(bookingData) {
      try {
        const response = await apiClient.post('/bookings', bookingData);
        return response.data;
      } catch (error) {
        console.error('Create booking error:', error);
        throw error;
      }
    },

    /**
     * Get booking by ID.
     * 
     * @param {number} bookingId - Booking ID
     * @returns {Promise<Object>} - Booking details
     */
    async getBooking(bookingId) {
      try {
        const response = await apiClient.get(`/bookings/${bookingId}`);
        return response.data;
      } catch (error) {
        console.error('Get booking error:', error);
        throw error;
      }
    },

    /**
     * Get booking by reference number.
     * 
     * @param {string} reference - Booking reference number
     * @returns {Promise<Object>} - Booking details
     */
    async getBookingByReference(reference) {
      try {
        const response = await apiClient.get(`/bookings/reference/${reference}`);
        return response.data;
      } catch (error) {
        console.error('Get booking by reference error:', error);
        throw error;
      }
    },

    /**
     * Get booking by tracking number.
     * 
     * @param {string} trackingNumber - Tracking number
     * @returns {Promise<Object>} - Booking details
     */
    async getBookingByTracking(trackingNumber) {
      try {
        const response = await apiClient.get(`/bookings/tracking/${trackingNumber}`);
        return response.data;
      } catch (error) {
        console.error('Get booking by tracking error:', error);
        throw error;
      }
    },

    /**
     * Get user's bookings.
     * 
     * @returns {Promise<Object>} - User's bookings list
     */
    async getUserBookings() {
      try {
        const response = await apiClient.get('/bookings/my-bookings');
        return response.data;
      } catch (error) {
        console.error('Get user bookings error:', error);
        throw error;
      }
    },

    /**
     * Confirm payment for a booking.
     * 
     * @param {number} bookingId - Booking ID
     * @param {Object} paymentData - Payment confirmation data
     * @returns {Promise<Object>} - Updated booking
     */
    async confirmPayment(bookingId, paymentData) {
      try {
        const response = await apiClient.post(`/bookings/${bookingId}/confirm-payment`, paymentData);
        return response.data;
      } catch (error) {
        console.error('Confirm payment error:', error);
        throw error;
      }
    },

    /**
     * Cancel a booking.
     * 
     * @param {number} bookingId - Booking ID
     * @returns {Promise<Object>} - Updated booking
     */
    async cancelBooking(bookingId) {
      try {
        const response = await apiClient.put(`/bookings/${bookingId}/cancel`);
        return response.data;
      } catch (error) {
        console.error('Cancel booking error:', error);
        throw error;
      }
    }
  }
};

export default apiService;
